import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import {
  isPasswordResetAccepted,
  type PasswordResetAccepted,
  type PasswordResetCompletion,
  type PasswordResetRequest,
} from '@/shared/api/generated/tenancy';
import { post, postNoContent } from '@/shared/api/http';

/* Asking for a password reset, and spending the token that comes back.
 *
 * **The types and guards are the generated ones.** They were hand-written while the contract landed
 * in parallel, and the three schemas were absent from `EMITTED` in
 * `tools/openapi_ts/generate.py` — the sixth time that registry has failed open, so a regeneration
 * would have written `tenancy.ts` without them and reported success. Registered and regenerated, and
 * this module is now its two hooks: a hand-written type beside a generated one is two answers to
 * what the server sends, which is the drift the generator exists to prevent.
 *
 * **`transport` is the contract's union rather than a widened `string`, and that is a deliberate
 * reversal.** The hand-written version widened it so an unrecognised value would reach `deliveryOf`
 * and be treated as undeliverable — the safe direction. The generated guard refuses the whole
 * response instead. That is the stricter behaviour and the consistent one: every other enum in the
 * 21 generated specs is guarded the same way, and hard rule 10 makes the contract the authority, so
 * a server that gains a third transport changes the spec and the client regenerates. `deliveryOf`
 * still fails toward undeliverable for the values it does receive, which is what matters on a screen
 * whose whole job is to say the same thing either way.
 *
 * The guard is not decoration. `getPage`'s rule applies to a mutation's answer too: `transport` is
 * the *only* thing distinguishing a reset that is on its way from one nothing can carry.
 *
 * **Neither route is authenticated**, and neither needs to be: the whole point is that the person
 * asking cannot sign in. `post` attaches a bearer token when one happens to exist, which is harmless
 * — the server ignores it on these two paths.
 */

const REQUEST_URL = '/v1/auth/password-reset-requests';
const RESET_URL = '/v1/auth/password-resets';

/* One fresh key per submission, matching `api/auth.ts`'s `freshKey` and for its stated reason: a
 * double-click reuses the in-flight mutation because `useMutation` tracks one call at a time, and a
 * genuinely new attempt is a new decision rather than a replay of one that already failed.
 *
 * **Only the request route requires one, and the completion route is sent one anyway.**
 * `tenancy.yaml` requires `Idempotency-Key` on `/password-reset-requests` — it creates a row and a
 * retry must not supersede the token the first attempt already mailed — and exempts
 * `/password-resets` through `NON_CREATING_POST_PATHS`, because that one moves a column on an
 * existing row under a guard, so a retry of a request that already succeeded is the ordinary 400.
 * The header is still sent there for the reason `useLogIn` states for `/login`: the exemption is
 * from the *requirement*, so an unnecessary key is accepted and ignored, and the alternative is a
 * transport helper that can post without one — which is the shape `post`'s required parameter exists
 * to make impossible. */
function freshKey(): string {
  return crypto.randomUUID();
}

/** Asks for a reset link. Always succeeds, which is the anti-enumeration property. */
export function useRequestPasswordReset(): UseMutationResult<
  PasswordResetAccepted,
  Error,
  PasswordResetRequest
> {
  return useMutation({
    mutationFn: (draft: PasswordResetRequest) =>
      post(
        REQUEST_URL,
        freshKey(),
        isPasswordResetAccepted,
        'password reset request',
        draft,
      ),
    retry: false,
  });
}

/** Spends a token and sets the password. 204, so there is nothing to read back.
 *
 * **No token is written to a query cache and no `onSuccess` stores a session.** A completed reset
 * does not sign anybody in: the caller is redirected to `/login` to use the password they just set,
 * which is the one action that proves it works and the only thing that makes the redirect honest.
 */
export function useResetPassword(): UseMutationResult<
  void,
  Error,
  PasswordResetCompletion
> {
  return useMutation({
    mutationFn: (draft: PasswordResetCompletion) =>
      postNoContent(RESET_URL, freshKey(), draft),
    retry: false,
  });
}
